import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import DailyQuote from "./components/DailyQuote";

const habitSteps = [
  {
    number: "01",
    title: "Choose a book",
    desc: "Pick something you genuinely want to read. Fiction, nonfiction, philosophy — the genre doesn't matter. The habit does.",
  },
  {
    number: "02",
    title: "Read ten pages",
    desc: "Open it. Read ten pages. Not a chapter, not a section — just ten pages. Consistent, measurable, repeatable.",
  },
  {
    number: "03",
    title: "Do it again tomorrow",
    desc: "The same time. The same place. No negotiation. That's it. Repeated every day, it becomes who you are.",
  },
];

export default function Home() {
  return (
    <main className="bg-[#F4F3F0]">
      <Navbar />

      {/* ─── HERO ─── */}
      <section className="relative min-h-screen w-full flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0cdef3babb17daa18103c78bff904930.jpg"
            alt="Reading habit lifestyle"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20" />
        </div>

        <div className="relative z-10 w-full px-8 lg:px-24 pt-24">
          <div className="max-w-2xl">
            <span className="inline-block text-[#6B9E8A] text-xs font-semibold tracking-[0.2em] uppercase mb-6 animate-fade-in-up">
              For the person who shows up.
            </span>
            <h1 className="mb-4 animate-fade-in-up-d1">
              <img
                src="https://public.readdy.ai/ai/img_res/cd5fb264-97ad-456c-b34b-2769ee1ae298.png"
                alt="10 Pages a Day"
                className="h-20 lg:h-28 w-auto object-contain"
              />
            </h1>
            <p className="text-white/85 text-xl lg:text-2xl font-serif italic mb-5 animate-fade-in-up-d2">
              You are what you do each day.
            </p>
            <p className="text-white/65 text-base lg:text-lg font-light leading-relaxed mb-10 max-w-md animate-fade-in-up-d3">
              This isn't about reading more. It's about becoming the kind of person who shows up for themselves — quietly, consistently, every single day.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up-d4">
              <Link
                to="/challenge"
                className="inline-flex items-center gap-2 bg-[#3A5544] text-white px-8 py-3.5 rounded font-medium hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Start the Habit
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/35 text-xs tracking-widest uppercase">
          <span>Scroll</span>
          <div className="w-px h-8 bg-white/25 animate-pulse" />
        </div>
      </section>

      {/* ─── BRAND IDEA ─── */}
      <section className="py-28 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div>
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block">The Idea</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.2] mb-8">
              Small habits.<br />Real change.
            </h2>
            <p className="text-[#555] text-base leading-[1.9] mb-5">
              Ten pages a day won't change your life overnight.
            </p>
            <p className="text-[#555] text-base leading-[1.9]">
              But it will change how you think, how you focus, and how you show up.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 h-52 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=stack%20of%20curated%20books%20arranged%20beautifully%20on%20a%20light%20wooden%20shelf%20with%20soft%20natural%20window%20light%2C%20calm%20minimalist%20aesthetic%2C%20neutral%20cream%20tones%20with%20green%20plant%20accent%2C%20lifestyle%20photography%20clean%20background&width=800&height=380&seq=idea-books-home&orientation=landscape"
                alt="Curated books"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="h-36 rounded-lg bg-[#3A5544] flex items-center justify-center">
              <div className="text-center">
                <p className="font-serif text-4xl text-white font-bold">47</p>
                <p className="text-white/60 text-xs mt-1 tracking-wide">books a year</p>
              </div>
            </div>
            <div className="h-36 rounded-lg bg-[#111111] flex items-center justify-center">
              <div className="text-center">
                <p className="font-serif text-4xl text-[#6B9E8A] font-bold">10K+</p>
                <p className="text-white/50 text-xs mt-1 tracking-wide">daily readers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── THE HABIT (3 STEPS) ─── */}
      <section className="py-24 px-8 lg:px-24 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <span className="text-[#6B9E8A] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block">The System</span>
          <h2 className="font-serif text-4xl lg:text-5xl text-white leading-[1.2] mb-16 max-w-xl">
            The Habit
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {habitSteps.map((step) => (
              <div key={step.number} className="border-t border-white/10 pt-8">
                <p className="font-serif text-5xl text-[#6B9E8A]/30 font-bold mb-6 leading-none">
                  {step.number}
                </p>
                <h3 className="font-serif text-xl text-white mb-4">{step.title}</h3>
                <p className="text-white/45 text-sm leading-[1.9]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DAILY QUOTE ─── */}
      <DailyQuote />

      {/* ─── CHALLENGE CTA ─── */}
      <section className="relative py-32 px-8 lg:px-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20flat%20lay%20of%20open%20book%20with%20dried%20botanical%20leaves%20on%20dark%20forest%20green%20textured%20linen%20surface%2C%20moody%20and%20calm%20atmosphere%2C%20minimal%20composition%2C%20dark%20green%20and%20cream%20palette%2C%20artistic%20editorial%20photography%20clean%20background&width=1920&height=800&seq=challenge-cta-bg&orientation=landscape"
            alt="Challenge background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#111111]/80" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <span className="text-[#6B9E8A] text-xs font-semibold uppercase tracking-[0.2em] mb-6 block">The Challenge</span>
          <h2 className="font-serif text-4xl lg:text-5xl text-white leading-[1.2] mb-6">
            Thousands of readers.<br />One simple habit.
          </h2>
          <p className="text-white/55 text-base leading-relaxed mb-10">
            Commit to 30 days. 10 pages a day. No exceptions. Join a community of readers who've made the decision to show up — every single day.
          </p>
          <Link
            to="/challenge"
            className="inline-flex items-center gap-2 bg-[#3A5544] text-white px-9 py-4 rounded font-medium hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Join the 30-Day Challenge
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
